import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as LucideIcons from 'lucide-react';

const GrievanceStepModal = ({ isOpen, onClose, onSubmit, step, loading }) => {
    const [formData, setFormData] = useState({
        step_number: '',
        title: '',
        description: '',
        timeline: '',
        icon: 'Building2',
        display_order: 0,
        is_active: true
    });

    useEffect(() => {
        if (step) {
            setFormData({
                step_number: step.step_number || '',
                title: step.title || '',
                description: step.description || '',
                timeline: step.timeline || '',
                icon: step.icon || 'Building2',
                display_order: step.display_order || 0,
                is_active: step.is_active !== undefined ? step.is_active : true
            });
        } else {
            setFormData({
                step_number: '',
                title: '',
                description: '',
                timeline: '',
                icon: 'Building2',
                display_order: 0,
                is_active: true
            });
        }
    }, [step, isOpen]);

    // Common icons list
    const iconOptions = ['Building2', 'User', 'Shield', 'Clock', 'FileText', 'Phone', 'Mail', 'MapPin', 'CheckCircle2'];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl transform transition-all">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h3 className="text-xl font-bold text-slate-800">
                        {step ? 'Edit Step' : 'Add New Step'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit(formData);
                }} className="p-6 space-y-4">

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Step Number</label>
                            <input
                                type="text"
                                value={formData.step_number}
                                onChange={(e) => setFormData({ ...formData, step_number: e.target.value })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="e.g. 01"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                            <input
                                type="number"
                                value={formData.display_order}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Title</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g. Branch Level"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g. Register at your branch"
                            rows="2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Timeline</label>
                        <input
                            type="text"
                            value={formData.timeline}
                            onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g. 15 working days"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Icon</label>
                        <div className="grid grid-cols-5 gap-2">
                            {iconOptions.map((iconName) => {
                                const Icon = LucideIcons[iconName];
                                return (
                                    <button
                                        key={iconName}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon: iconName })}
                                        className={`p-3 rounded-xl border flex items-center justify-center transition-all ${formData.icon === iconName
                                                ? 'border-primary-500 bg-primary-50 text-primary-600'
                                                : 'border-slate-200 hover:border-slate-300 text-slate-500'
                                            }`}
                                    >
                                        <Icon className="h-5 w-5" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                            Active
                        </label>
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-all shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Saving...' : (step ? 'Update Step' : 'Add Step')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GrievanceStepModal;
